'use client';

import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import {
  FiCheckCircle,
  FiAlertTriangle,
  FiDownload,
  FiInfo,
} from 'react-icons/fi';
import { iconVariants, modalVariants } from '../../auth/signup/motion';

import { fetchInvoices } from '../../../redux/features/invoiceSlice';
import { updateApplication } from '../../../redux/features/applicationSlice';
import { setApplicationData } from '../../../redux/features/newApplicationSlice';
import { forceLogout } from '../../../utlis/forceLogout';
import {
  fetchChecklistDetails,
  selectCurrentChecklist,
  selectIsLoading as selectChecklistLoading,
  clearCurrentChecklist,
} from '../../../redux/features/clientChecklistSlice';

const DocumentTab = ({ propsData, setActiveTab, onRefresh }) => {
  const FIELD_LIMITS = {
    mainCategoryInput: 60,
    subCategoryInput: 60,
    documentName: 100,
  };

  const clampValue = (value, maxLength) => {
    if (typeof value !== 'string') return value;
    if (!maxLength) return value;
    return value.slice(0, maxLength);
  };

  const sanitizeTextValue = (value, fieldKey) => {
    if (typeof value !== 'string') return value;
    const cleaned = value.replace(/[\u0000-\u001F\u007F]/g, '');
    return clampValue(cleaned, FIELD_LIMITS[fieldKey]);
  };

  const selectedData = propsData;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const userRole = JSON.parse(localStorage.getItem('user'))?.role;
  const newData = useSelector((state) => state.createApplication.newData);
  const appId = newData.id !== 'new' && newData.id !== '' ? newData.id : id;
  const { applications, isLoading, error } = useSelector(
    (state) => state.application
  );

  // Checklist Data
  const checklist = useSelector(selectCurrentChecklist);
  const checklistLoading = useSelector(selectChecklistLoading);

  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Clear the current checklist when component mounts
  useEffect(() => {
    dispatch(clearCurrentChecklist());
  }, [dispatch]);

  // Fetch checklist data when component mounts or when country/visaCategory changes
  useEffect(() => {
    if (selectedData?.countryOfInterest && selectedData?.visaCategory) {
      dispatch(
        fetchChecklistDetails({
          country: selectedData.countryOfInterest,
          visaCategory: selectedData.visaCategory,
        })
      );
    }
  }, [selectedData?.countryOfInterest, selectedData?.visaCategory, dispatch]);




  const handleDownloadChecklist = (checklist) => {
    const fileUrl =
      checklist?.checkListDocument || checklist?.document?.location;
    const fileName =
      checklist?.checkListDocumentName ||
      checklist?.title ||
      'checklist-document';

    if (!fileUrl) {
      window.alert('No checklist document found');
      return;
    }

    fetch(fileUrl)
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to download checklist document');
        }
        return response.blob();
      })
      .then((blob) => {
        saveAs(blob, fileName);
      })
      .catch((error) => {
        console.error('Error downloading checklist document:', error);
        window.open(fileUrl, '_blank');
      });
  };

  // Local documents state — updated immediately after upload
  const [localDocuments, setLocalDocuments] = useState(
    selectedData?.documents || []
  );

  useEffect(() => {
    if (selectedData?.documents) {
      setLocalDocuments(selectedData.documents);
    }
  }, [selectedData?.documents]);

  // Category management
  const [categoryList, setCategoryList] = useState([]);
  const [mainCategoryInput, setMainCategoryInput] = useState('');
  const [selectedMainId, setSelectedMainId] = useState(null);
  const [subCategoryInput, setSubCategoryInput] = useState('');
  const [selectedSubId, setSelectedSubId] = useState(null);
  const [documentName, setDocumentName] = useState('');
  const GENERAL_MAIN_CATEGORY_NAME = 'General Documents';
  const [isMainSuggestionOpen, setIsMainSuggestionOpen] = useState(false);
  const [isSubSuggestionOpen, setIsSubSuggestionOpen] = useState(false);
  const mainWrapperRef = useRef(null);
  const subWrapperRef = useRef(null);
  const rarWasmBinaryRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        mainWrapperRef.current &&
        !mainWrapperRef.current.contains(event.target)
      ) {
        setIsMainSuggestionOpen(false);
      }
      if (
        subWrapperRef.current &&
        !subWrapperRef.current.contains(event.target)
      ) {
        setIsSubSuggestionOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const mainSuggestions = categoryList.filter((c) =>
    c.name.toLowerCase().includes(mainCategoryInput.toLowerCase())
  );

  const selectedMain = categoryList.find((c) => c._id === selectedMainId);

  const subSuggestions = (selectedMain?.children || []).filter((s) =>
    s.name.toLowerCase().includes(subCategoryInput.toLowerCase())
  );

  const ensureGeneralMainCategory = async (categories = []) => {
    const hasGeneralDocuments = categories.some(
      (category) => category?.name === GENERAL_MAIN_CATEGORY_NAME
    );

    if (hasGeneralDocuments) {
      return categories;
    }

    try {
      const createResponse = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/documentcategory`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify({ name: GENERAL_MAIN_CATEGORY_NAME }),
        }
      );

      const createData = await createResponse.json();
      if (handleSessionExpiry(createData)) return categories;

      const refreshedResponse = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/documentcategory`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      const refreshedData = await refreshedResponse.json();
      if (handleSessionExpiry(refreshedData)) return categories;

      return refreshedData?.body?.categories || categories;
    } catch (error) {
      console.error('Error ensuring General Documents category:', error);
      return categories;
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/documentcategory`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      const data = await response.json();
      if (handleSessionExpiry(data)) return;
      if (data?.body?.categories) {
        const ensuredCategories = await ensureGeneralMainCategory(
          data.body.categories
        );
        setCategoryList(ensuredCategories);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleSessionExpiry = (data) => {
    if (data?.code === 401) {
      forceLogout();
      return true;
    }
    return false;
  };

  // File Upload State
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleMainInputChange = (e) => {
    setMainCategoryInput(
      sanitizeTextValue(e.target.value, 'mainCategoryInput')
    );
    setSelectedMainId(null);
    setIsMainSuggestionOpen(true);
    setSubCategoryInput('');
    setSelectedSubId(null);
  };

  const handleSubInputChange = (e) => {
    setSubCategoryInput(sanitizeTextValue(e.target.value, 'subCategoryInput'));
    setSelectedSubId(null);
    setIsSubSuggestionOpen(true);
  };

  const ALLOWED_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ];
  const ALLOWED_EXTENSIONS = ['.pdf', '.doc', '.docx', '.docs'];
  const ARCHIVE_EXTENSIONS = ['.zip', '.rar'];
  const MAX_SIZE_MB = 5;
  const MAX_ARCHIVE_SIZE_MB = 100;

  const MIME_BY_EXTENSION = {
    '.pdf': 'application/pdf',
    '.doc': 'application/msword',
    '.docx':
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    '.docs':
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  };

  const getExtension = (name = '') => {
    const cleanName = `${name}`.trim();
    if (!cleanName.includes('.')) return '';
    return `.${cleanName.split('.').pop().toLowerCase()}`;
  };

  const getBaseName = (name = '') => {
    const cleanName = `${name}`.trim();
    if (!cleanName) return 'Document';
    const lastDot = cleanName.lastIndexOf('.');
    if (lastDot <= 0) return cleanName;
    return cleanName.slice(0, lastDot);
  };

  const isArchiveFile = (fileName = '') =>
    ARCHIVE_EXTENSIONS.includes(getExtension(fileName));

  const isAllowedDocument = (fileName = '', mimeType = '') => {
    const ext = getExtension(fileName);
    return ALLOWED_EXTENSIONS.includes(ext) || ALLOWED_TYPES.includes(mimeType);
  };

  const validateFile = (file) => {
    const ext = getExtension(file.name);
    const isArchive = ARCHIVE_EXTENSIONS.includes(ext);

    if (!isArchive && !isAllowedDocument(file.name, file.type)) {
      alert('Only PDF, DOC, DOCX, DOCS, ZIP, or RAR files are allowed.');
      return false;
    }

    if (isArchive && file.size > MAX_ARCHIVE_SIZE_MB * 1024 * 1024) {
      alert(`Archive size must not exceed ${MAX_ARCHIVE_SIZE_MB}MB.`);
      return false;
    }

    if (!isArchive && file.size > MAX_SIZE_MB * 1024 * 1024) {
      alert(`File size must not exceed ${MAX_SIZE_MB}MB.`);
      return false;
    }

    return true;
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      if (!validateFile(file)) {
        e.target.value = '';
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      if (!validateFile(file)) return;
      setSelectedFile(file);
    }
  };

  const isArchiveUploadSelected =
    !!selectedFile && isArchiveFile(selectedFile.name);

  const RAR_WASM_PUBLIC_PATH = '/unrar.wasm';

  const loadRarWasmBinary = async () => {
    if (rarWasmBinaryRef.current) {
      return rarWasmBinaryRef.current;
    }

    const wasmResponse = await fetch(RAR_WASM_PUBLIC_PATH);
    if (!wasmResponse.ok) {
      throw new Error('RAR runtime file could not be loaded.');
    }

    const wasmBinary = await wasmResponse.arrayBuffer();
    rarWasmBinaryRef.current = wasmBinary;
    return wasmBinary;
  };

  const detectArchiveType = async (archiveFile) => {
    try {
      const signature = new Uint8Array(
        await archiveFile.slice(0, 8).arrayBuffer()
      );

      // ZIP signatures start with PK.
      if (signature[0] === 0x50 && signature[1] === 0x4b) {
        return 'zip';
      }

      // RAR signatures start with Rar!
      if (
        signature[0] === 0x52 &&
        signature[1] === 0x61 &&
        signature[2] === 0x72 &&
        signature[3] === 0x21
      ) {
        return 'rar';
      }
    } catch (error) {
      console.warn('Could not detect archive signature:', error);
    }

    const ext = getExtension(archiveFile?.name);
    if (ext === '.zip') return 'zip';
    if (ext === '.rar') return 'rar';
    return 'unknown';
  };

  const parseArchiveFilePath = (entryPath) => {
    const normalized = `${entryPath || ''}`.replace(/\\/g, '/');
    const parts = normalized.split('/').filter(Boolean);
    if (parts.length === 0) return null;

    const fileName = parts[parts.length - 1];
    const folderParts = parts.slice(0, -1);

    if (!fileName) return null;

    if (folderParts.length === 0) {
      return {
        mainCategoryName: GENERAL_MAIN_CATEGORY_NAME,
        subCategoryName: '',
        fileName,
      };
    }

    return {
      mainCategoryName: folderParts[0] || GENERAL_MAIN_CATEGORY_NAME,
      // Third-level and deeper folders are intentionally ignored.
      subCategoryName: folderParts[1] || '',
      fileName,
    };
  };

  const extractZipUploadItems = async (archiveFile) => {
    const zip = await JSZip.loadAsync(archiveFile);
    const items = [];
    let skippedUnsupported = 0;

    for (const entry of Object.values(zip.files)) {
      if (entry.dir) continue;

      const parsed = parseArchiveFilePath(entry.name);
      if (!parsed) continue;

      if (
        !isAllowedDocument(
          parsed.fileName,
          MIME_BY_EXTENSION[getExtension(parsed.fileName)] || ''
        )
      ) {
        skippedUnsupported++;
        continue;
      }

      const blob = await entry.async('blob');
      const ext = getExtension(parsed.fileName);
      const uploadFile = new File([blob], parsed.fileName, {
        type: MIME_BY_EXTENSION[ext] || blob.type || 'application/octet-stream',
      });

      items.push({
        file: uploadFile,
        mainCategoryName: parsed.mainCategoryName,
        subCategoryName: parsed.subCategoryName,
        docName: getBaseName(parsed.fileName),
      });
    }

    return { items, skippedUnsupported };
  };

  const extractRarUploadItems = async (archiveFile) => {
    const unrarModule = await import('node-unrar-js/esm/index.esm.js');
    const { createExtractorFromData } = unrarModule;
    const archiveBuffer = await archiveFile.arrayBuffer();
    const wasmBinary = await loadRarWasmBinary();
    const extractor = await createExtractorFromData({
      data: archiveBuffer,
      wasmBinary,
    });
    const extracted = extractor.extract({});

    const items = [];
    let skippedUnsupported = 0;

    for (const fileEntry of extracted.files) {
      const header = fileEntry?.fileHeader;
      if (header?.flags?.directory) continue;

      const filePath = header?.name || '';
      const parsed = parseArchiveFilePath(filePath);
      if (!parsed) continue;

      const ext = getExtension(parsed.fileName);
      if (!isAllowedDocument(parsed.fileName, MIME_BY_EXTENSION[ext] || '')) {
        skippedUnsupported++;
        continue;
      }

      const bytes = fileEntry?.extraction;
      if (!bytes) continue;

      const mimeType = MIME_BY_EXTENSION[ext] || 'application/octet-stream';
      const blob = new Blob([bytes], { type: mimeType });
      const uploadFile = new File([blob], parsed.fileName, { type: mimeType });

      items.push({
        file: uploadFile,
        mainCategoryName: parsed.mainCategoryName,
        subCategoryName: parsed.subCategoryName,
        docName: getBaseName(parsed.fileName),
      });
    }

    return { items, skippedUnsupported };
  };

  const getArchiveUploadItems = async (archiveFile) => {
    const archiveType = await detectArchiveType(archiveFile);

    if (archiveType === 'zip') {
      return extractZipUploadItems(archiveFile);
    }

    if (archiveType === 'rar') {
      return extractRarUploadItems(archiveFile);
    }

    throw new Error('Unsupported or invalid archive file.');
  };

  const fetchCategoriesSnapshot = async () => {
    const response = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/documentcategory`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }
    );
    const data = await response.json();
    if (handleSessionExpiry(data)) return null;
    return data?.body?.categories || [];
  };

  const resolveMainCategoryId = async (categoriesRef, mainCategoryName) => {
    const normalizedName = (
      mainCategoryName || GENERAL_MAIN_CATEGORY_NAME
    ).trim();
    if (!normalizedName) return null;

    const existingMain = categoriesRef.value.find(
      (c) => c?.name?.toLowerCase() === normalizedName.toLowerCase()
    );
    if (existingMain?._id) return existingMain._id;

    const createRes = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/documentcategory`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ name: normalizedName }),
      }
    );

    const createData = await createRes.json();
    if (handleSessionExpiry(createData)) return null;

    let createdId =
      createData?.body?._id || createData?.category?._id || createData?._id;
    if (createdId) {
      categoriesRef.value = [
        ...categoriesRef.value,
        { _id: createdId, name: normalizedName, children: [] },
      ];
      return createdId;
    }

    const refreshed = await fetchCategoriesSnapshot();
    if (!refreshed) return null;
    categoriesRef.value = refreshed;
    setCategoryList(refreshed);

    const refreshedMain = refreshed.find(
      (c) => c?.name?.toLowerCase() === normalizedName.toLowerCase()
    );
    return refreshedMain?._id || null;
  };

  const resolveSubCategoryId = async (
    categoriesRef,
    mainId,
    subCategoryName
  ) => {
    const normalizedSub = (subCategoryName || '').trim();
    if (!normalizedSub) return null;

    const mainCategory = categoriesRef.value.find(
      (c) => String(c?._id) === String(mainId)
    );
    const existingSub = mainCategory?.children?.find(
      (child) => child?.name?.toLowerCase() === normalizedSub.toLowerCase()
    );
    if (existingSub?._id) return existingSub._id;

    const createRes = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/documentcategory`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ name: normalizedSub, parent: mainId }),
      }
    );

    const createData = await createRes.json();
    if (handleSessionExpiry(createData)) return null;

    let createdId =
      createData?.body?._id || createData?.category?._id || createData?._id;
    if (createdId) {
      categoriesRef.value = categoriesRef.value.map((category) => {
        if (String(category?._id) !== String(mainId)) return category;
        return {
          ...category,
          children: [
            ...(category?.children || []),
            { _id: createdId, name: normalizedSub },
          ],
        };
      });
      return createdId;
    }

    const refreshed = await fetchCategoriesSnapshot();
    if (!refreshed) return null;
    categoriesRef.value = refreshed;
    setCategoryList(refreshed);

    const refreshedMain = refreshed.find(
      (c) => String(c?._id) === String(mainId)
    );
    const refreshedSub = refreshedMain?.children?.find(
      (child) => child?.name?.toLowerCase() === normalizedSub.toLowerCase()
    );
    return refreshedSub?._id || null;
  };

  const uploadDocumentWithCategory = async (
    categoriesRef,
    file,
    mainCategoryName,
    subCategoryName,
    docTitle,
    mainCategoryId,
    subCategoryId
  ) => {
    const safeMainCategoryName = sanitizeTextValue(
      mainCategoryName,
      'mainCategoryInput'
    );
    const safeSubCategoryName = sanitizeTextValue(
      subCategoryName,
      'subCategoryInput'
    );
    const safeDocTitle = sanitizeTextValue(
      docTitle || getBaseName(file?.name),
      'documentName'
    );

    const mainId =
      mainCategoryId ||
      (await resolveMainCategoryId(categoriesRef, safeMainCategoryName));
    if (!mainId) {
      throw new Error('Could not resolve main category ID');
    }

    const subId =
      subCategoryId ||
      (await resolveSubCategoryId(categoriesRef, mainId, safeSubCategoryName));

    const formData = new FormData();
    formData.append('documents', file);
    formData.append('docName', safeDocTitle);
    formData.append('documentCategory', String(mainId));
    if (subId) formData.append('documentSubcategory', String(subId));

    const updateRes = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/application/updateApplication/${selectedData._id}`,
      {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: formData,
      }
    );

    if (!updateRes.ok) {
      const errorText = await updateRes.text();
      throw new Error(`Update failed: ${updateRes.status} ${errorText}`);
    }

    const updateData = await updateRes.json();
    if (handleSessionExpiry(updateData)) return null;

    return (
      updateData?.body?.application ||
      updateData?.body ||
      updateData?.application ||
      updateData?.data ||
      null
    );
  };

  const resetUploadForm = () => {
    setMainCategoryInput('');
    setSelectedMainId(null);
    setSubCategoryInput('');
    setSelectedSubId(null);
    setDocumentName('');
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleCategorySubmit = async () => {
    if (!selectedFile || !selectedData?._id) return;

    setIsSubmitting(true);
    try {
      const categoriesRef = {
        value: categoryList.length ? [...categoryList] : [],
      };

      if (categoriesRef.value.length === 0) {
        const fetchedCategories = await fetchCategoriesSnapshot();
        if (!fetchedCategories) return;
        categoriesRef.value = fetchedCategories;
        setCategoryList(fetchedCategories);
      }

      const uploadQueue = [];
      let skippedUnsupported = 0;

      if (isArchiveFile(selectedFile.name)) {
        const archiveResult = await getArchiveUploadItems(selectedFile);
        uploadQueue.push(...archiveResult.items);
        skippedUnsupported = archiveResult.skippedUnsupported;

        if (uploadQueue.length === 0) {
          alert(
            'No supported documents found in archive. Only PDF, DOC, DOCX, and DOCS are uploaded.'
          );
          return;
        }
      } else {
        uploadQueue.push({
          file: selectedFile,
          mainCategoryName:
            sanitizeTextValue(mainCategoryInput, 'mainCategoryInput').trim() ||
            GENERAL_MAIN_CATEGORY_NAME,
          subCategoryName: sanitizeTextValue(
            subCategoryInput,
            'subCategoryInput'
          ).trim(),
          docName:
            sanitizeTextValue(documentName, 'documentName').trim() ||
            getBaseName(selectedFile.name),
          mainCategoryId: selectedMainId,
          subCategoryId: selectedSubId,
        });
      }

      let successCount = 0;
      let failCount = 0;
      let latestApplication = null;

      for (const item of uploadQueue) {
        try {
          const uploadedApplication = await uploadDocumentWithCategory(
            categoriesRef,
            item.file,
            item.mainCategoryName,
            item.subCategoryName,
            item.docName,
            item.mainCategoryId,
            item.subCategoryId
          );

          if (!uploadedApplication) {
            return;
          }

          latestApplication = uploadedApplication;
          successCount++;
        } catch (uploadError) {
          console.error(
            'Upload failed for file:',
            item?.file?.name,
            uploadError
          );
          failCount++;
        }
      }

      if (latestApplication?.documents) {
        setLocalDocuments(latestApplication.documents);
      }

      const refreshedCategories = await fetchCategoriesSnapshot();
      if (refreshedCategories) {
        setCategoryList(refreshedCategories);
      }

      // Trigger refresh on parent so propsData stays in sync
      if (onRefresh && latestApplication) {
        onRefresh(latestApplication);
      }

      if (successCount > 0) {
        resetUploadForm();
      }

      if (isArchiveFile(selectedFile.name)) {
        alert(
          `Archive upload complete. Uploaded ${successCount} file(s), failed ${failCount} file(s), skipped ${skippedUnsupported} unsupported file(s).`
        );
      } else if (failCount > 0 && successCount === 0) {
        alert('Document upload failed. Please try again.');
      }
    } catch (error) {
      if (isArchiveFile(selectedFile?.name)) {
        console.error('Archive processing failed:', error);
        alert(
          'Could not process archive. Please ensure ZIP/RAR is valid and try again.'
        );
      } else {
        console.error('Error managing categories:', error);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const [isDownloadingZip, setIsDownloadingZip] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const ZIP_DUPLICATE_SUFFIX = '\u200B';
  const ZIP_LOOKALIKE_CHAR_MAP = {
    '/': '／',
    '\\': '＼',
    '?': '？',
    '%': '％',
    '*': '＊',
    ':': '：',
    '|': '｜',
    '"': '＂',
    '<': '＜',
    '>': '＞',
  };

  const normalizeZipSegment = (value, fallback) => {
    const raw = `${value ?? ''}`.trim();
    const safeRaw = raw || fallback;
    return safeRaw.replace(
      /[\/\\?%*:|"<>]/g,
      (char) => ZIP_LOOKALIKE_CHAR_MAP[char] || '-'
    );
  };

  const getUniqueDisplaySegment = (registry, parentPath, segment) => {
    const key = `${parentPath}::${segment}`;
    const count = registry.get(key) || 0;
    registry.set(key, count + 1);
    if (count === 0) return segment;
    return `${segment}${ZIP_DUPLICATE_SUFFIX.repeat(count)}`;
  };

  const getUniqueDisplayFileName = (registry, folderPath, fileName) => {
    const key = `${folderPath}::${fileName}`;
    const count = registry.get(key) || 0;
    registry.set(key, count + 1);

    if (count === 0) return fileName;

    const lastDotIndex = fileName.lastIndexOf('.');
    if (lastDotIndex > 0) {
      const base = fileName.slice(0, lastDotIndex);
      const ext = fileName.slice(lastDotIndex);
      return `${base}${ZIP_DUPLICATE_SUFFIX.repeat(count)}${ext}`;
    }

    return `${fileName}${ZIP_DUPLICATE_SUFFIX.repeat(count)}`;
  };

  const handleDownloadZip = async () => {
    if (!localDocuments || localDocuments.length === 0) {
      alert('No documents to download.');
      return;
    }
    setIsDownloadingZip(true);
    let successCount = 0;
    let failCount = 0;
    try {
      const zip = new JSZip();
      const folderNameRegistry = new Map();
      const fileNameRegistry = new Map();
      const categoryPathById = new Map();
      const subcategoryPathById = new Map();

      for (const category of categoryList) {
        const categoryId = category?._id ? String(category._id) : null;
        const categorySegment = normalizeZipSegment(
          category?.name,
          'Uncategorized'
        );
        const uniqueCategorySegment = getUniqueDisplaySegment(
          folderNameRegistry,
          '',
          categorySegment
        );
        const categoryPath = uniqueCategorySegment;

        if (categoryId) {
          categoryPathById.set(categoryId, categoryPath);
        }

        for (const child of category?.children || []) {
          const childId = child?._id ? String(child._id) : null;
          const childSegment = normalizeZipSegment(
            child?.name,
            'No Subcategory'
          );
          const uniqueChildSegment = getUniqueDisplaySegment(
            folderNameRegistry,
            categoryPath,
            childSegment
          );
          if (categoryId && childId) {
            subcategoryPathById.set(
              `${categoryId}::${childId}`,
              `${categoryPath}/${uniqueChildSegment}`
            );
          }
        }
      }

      const resolveFolderPath = (doc) => {
        const docCatId = doc.documentCategory?._id || doc.documentCategory;
        const docSubId =
          doc.documentSubcategory?._id || doc.documentSubcategory;
        const normalizedCatId = docCatId ? String(docCatId) : null;
        const normalizedSubId = docSubId ? String(docSubId) : null;

        if (normalizedCatId && normalizedSubId) {
          const mappedSubPath = subcategoryPathById.get(
            `${normalizedCatId}::${normalizedSubId}`
          );
          if (mappedSubPath) return mappedSubPath;
        }

        if (normalizedCatId) {
          const mappedCatPath = categoryPathById.get(normalizedCatId);
          if (mappedCatPath) {
            if (normalizedSubId) {
              const rawSubName =
                doc.documentSubcategory?.name || 'No Subcategory';
              const subSegment = normalizeZipSegment(
                rawSubName,
                'No Subcategory'
              );
              const uniqueSubSegment = getUniqueDisplaySegment(
                folderNameRegistry,
                mappedCatPath,
                subSegment
              );
              const createdSubPath = `${mappedCatPath}/${uniqueSubSegment}`;
              subcategoryPathById.set(
                `${normalizedCatId}::${normalizedSubId}`,
                createdSubPath
              );
              return createdSubPath;
            }

            return mappedCatPath;
          }
        }

        const rawCategoryName = doc.documentCategory?.name || 'Uncategorized';
        const categorySegment = normalizeZipSegment(
          rawCategoryName,
          'Uncategorized'
        );
        const uniqueCategorySegment = getUniqueDisplaySegment(
          folderNameRegistry,
          '',
          categorySegment
        );
        const createdCategoryPath = uniqueCategorySegment;

        if (normalizedCatId) {
          categoryPathById.set(normalizedCatId, createdCategoryPath);
        }

        if (!normalizedSubId) {
          return createdCategoryPath;
        }

        const rawSubName = doc.documentSubcategory?.name || 'No Subcategory';
        const subSegment = normalizeZipSegment(rawSubName, 'No Subcategory');
        const uniqueSubSegment = getUniqueDisplaySegment(
          folderNameRegistry,
          createdCategoryPath,
          subSegment
        );
        const createdSubPath = `${createdCategoryPath}/${uniqueSubSegment}`;

        if (normalizedCatId && normalizedSubId) {
          subcategoryPathById.set(
            `${normalizedCatId}::${normalizedSubId}`,
            createdSubPath
          );
        }

        return createdSubPath;
      };

      for (const doc of localDocuments) {
        const folderPath = resolveFolderPath(doc);

        if (!doc.location) continue;
        try {
          const res = await fetch(doc.location);
          if (!res.ok) {
            console.warn(`HTTP ${res.status} fetching:`, doc.location);
            failCount++;
            continue;
          }
          const blob = await res.blob();
          if (!blob || blob.size === 0) {
            console.warn('Empty blob for:', doc.location);
            failCount++;
            continue;
          }
          const urlParts = doc.location.split('/');
          const rawFileName = decodeURIComponent(
            urlParts[urlParts.length - 1].split('?')[0]
          );
          const ext = rawFileName.includes('.')
            ? '.' + rawFileName.split('.').pop()
            : '';
          const rawBaseName =
            doc.docName || doc.name || rawFileName || 'document';
          const baseName = normalizeZipSegment(rawBaseName, 'document');
          const intendedFileName = baseName
            .toLowerCase()
            .endsWith(ext.toLowerCase())
            ? baseName
            : `${baseName}${ext}`;
          const uniqueFileName = getUniqueDisplayFileName(
            fileNameRegistry,
            folderPath,
            intendedFileName
          );
          zip.folder(folderPath).file(uniqueFileName, blob);
          successCount++;
        } catch (fetchErr) {
          console.warn(
            'Could not fetch document (CORS or network error):',
            doc.location,
            fetchErr
          );
          failCount++;
        }
      }

      if (successCount === 0) {
        alert(
          `No files could be fetched (${failCount} failed). ` +
            'This is likely a cross-origin (CORS) restriction on the file storage. ' +
            'Please contact your administrator to configure CORS on the storage bucket.'
        );
        return;
      }

      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, `${selectedData?.firstName} ${selectedData?.lastName || 'export'}.zip`);
      if (failCount > 0) {
        alert(
          `ZIP downloaded with ${successCount} file(s). ${failCount} file(s) could not be fetched and were skipped.`
        );
      }
    } catch (err) {
      console.error('Error generating ZIP:', err);
      alert('Failed to generate ZIP. Please try again.');
    } finally {
      setIsDownloadingZip(false);
    }
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const response = await fetch(doc.location);
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      saveAs(blob, doc.docName || doc.name || 'document');
    } catch (error) {
      console.error('Error downloading document:', error);
      // Fallback: just open in new tab
      window.open(doc.location, '_blank');
    }
  };

  const handleDeleteDocument = async (fileId) => {
    if (!window.confirm('Are you sure you want to delete this document?'))
      return;
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/application/deleteFile`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify({ applicationId: selectedData._id, fileId }),
        }
      );
      const data = await res.json();
      if (handleSessionExpiry(data)) return;
      const freshApplication =
        data?.body?.application ||
        data?.body ||
        data?.application ||
        data?.data ||
        null;
      if (freshApplication?.documents) {
        setLocalDocuments(freshApplication.documents);
      }
      if (onRefresh) onRefresh(freshApplication);
    } catch (error) {
      console.error('Error deleting document:', error);
    }
  };
  const handleView = (fileUrl) => {
    const viewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(
      fileUrl
    )}`;
    window.open(viewerUrl, '_blank');
  };
  // Submit Handler
  const handleSubmit = async () => {
    // Check if the application status allows resubmission
    // Only allow resubmission if status is "Draft"
    const nonResubmittableStatuses = [
      'WithdrawnByClient',
      'WithdrawnByAgent',
      'In-Progress',
      'Finalized',
      'Rejected',
      'Submitted',
    ];

    if (nonResubmittableStatuses.includes(propsData.status)) {
      alert(
        'This application cannot be resubmitted. Only Draft applications can be submitted.'
      );
      return;
    }

    try {
      if (checklist) {
        setLoading(true);

        const updateResult = await dispatch(
          updateApplication({
            id: appId,
            formData: {
              status: 'Submitted',
              submittedDate: Date.now(),
            },
            preventLoader: true,
          })
        );
        // Extract updated application from update result (handle different payload shapes)
        const updatedApp =
          updateResult?.payload?.data?.body

        const latestStatus =
          updatedApp?.status

        // Send POST request to generate invoice
        await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/application/generateInvoice`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
            body: JSON.stringify({ applicationId: appId }),
          }
        );

        const appData={
...newData, isSubmit: true
        }
        if(!!latestStatus){
          appData.status=latestStatus
      }
        await dispatch(setApplicationData(appData));

        setShowSuccess(true);
      } else {
        alert('Checklist is not available for this country and visa type.');
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handlecloseSuccess = () => {
    setShowSuccess(false);
    dispatch(fetchInvoices());
    setActiveTab('invoice');
  };
  // const visibleCategories = userRole === 'client'
  //   ? categoryList.filter((cat) => {
  //       if (cat.name === GENERAL_MAIN_CATEGORY_NAME) return true;
  //       return localDocuments?.some((doc) => {
  //         const docCatId = doc.documentCategory?._id || doc.documentCategory;
  //         return String(docCatId) === String(cat._id);
  //       });
  //     })
  //   : categoryList;
  const visibleCategories = categoryList.filter((cat) =>
    localDocuments?.some((doc) => {
      const docCatId = doc.documentCategory?._id || doc.documentCategory;
      return String(docCatId) === String(cat._id);
    })
  );
  return (
    <div className="p-3 md:p-6 rounded-b-lg shadow-sm bg-[#FFFBF5] border border-[#CAC3BB] text-sm md:text-base">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="space-y-6"
      >
        <motion.p
          className="text-[#005787] hover:underline block mb-8 cursor-pointer"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          onClick={() => handleDownloadChecklist(checklist)}
        >
          {checklistLoading
            ? 'Loading checklist...'
            : 'Click here to download checklist for all required documents that need to be submitted'}
        </motion.p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6 items-start">
          {/* Left Column: Category Inputs */}
          <div className="flex flex-col gap-4">
            <div className="relative" ref={mainWrapperRef}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <input
                value={mainCategoryInput}
                onChange={handleMainInputChange}
                onFocus={() => {
                  if (!isArchiveUploadSelected) setIsMainSuggestionOpen(true);
                }}
                disabled={isArchiveUploadSelected}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#005787] focus:border-[#005787] disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                placeholder="Select or Create..."
              />
              {isMainSuggestionOpen && !isArchiveUploadSelected && (
                <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 mt-1 rounded-md shadow-lg max-h-60 overflow-y-auto z-10">
                  {mainSuggestions.map((cat, idx) => (
                    <div
                      key={cat._id || idx}
                      className="p-3 hover:bg-gray-100 cursor-pointer border-b last:border-0 text-sm sm:text-base"
                      onClick={(e) => {
                        e.stopPropagation();
                        setMainCategoryInput(
                          sanitizeTextValue(cat.name, 'mainCategoryInput')
                        );
                        setSelectedMainId(cat._id);
                        setIsMainSuggestionOpen(false);
                        setSubCategoryInput('');
                        setSelectedSubId(null);
                      }}
                    >
                      {cat.name}
                    </div>
                  ))}

                  {mainCategoryInput &&
                    !categoryList.some(
                      (s) =>
                        s.name.toLowerCase() ===
                        mainCategoryInput.trim().toLowerCase()
                    ) && (
                      <div
                        className="p-3 hover:bg-gray-100 cursor-pointer text-[#036494] font-medium text-sm sm:text-base"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsMainSuggestionOpen(false);
                        }}
                      >
                        Add "{mainCategoryInput}" to the list
                      </div>
                    )}

                  {mainSuggestions.length === 0 && !mainCategoryInput && (
                    <div className="p-3 text-gray-500 italic text-sm sm:text-base">
                      Start typing to see suggestions...
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="relative" ref={subWrapperRef}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sub Category
              </label>
              <input
                value={subCategoryInput}
                onChange={handleSubInputChange}
                onFocus={() => {
                  if (!isArchiveUploadSelected) setIsSubSuggestionOpen(true);
                }}
                disabled={isArchiveUploadSelected}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#005787] focus:border-[#005787] disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                placeholder="Select or Create..."
              />
              {isSubSuggestionOpen && !isArchiveUploadSelected && (
                <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 mt-1 rounded-md shadow-lg max-h-60 overflow-y-auto z-10">
                  {subSuggestions.map((sub, idx) => (
                    <div
                      key={sub._id || idx}
                      className="p-3 hover:bg-gray-100 cursor-pointer border-b last:border-0 text-sm sm:text-base"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSubCategoryInput(
                          sanitizeTextValue(sub.name, 'subCategoryInput')
                        );
                        setSelectedSubId(sub._id);
                        setIsSubSuggestionOpen(false);
                      }}
                    >
                      {sub.name}
                    </div>
                  ))}

                  {subCategoryInput &&
                    !(selectedMain?.children || []).some(
                      (s) =>
                        s.name.toLowerCase() ===
                        subCategoryInput.trim().toLowerCase()
                    ) && (
                      <div
                        className="p-3 hover:bg-gray-100 cursor-pointer text-[#036494] font-medium text-sm sm:text-base"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsSubSuggestionOpen(false);
                        }}
                      >
                        Add "{subCategoryInput}" to the list
                      </div>
                    )}

                  {!selectedMain && (
                    <div className="p-3 text-gray-400 italic text-sm sm:text-base">
                      Select a valid Main Category first
                    </div>
                  )}

                  {selectedMain &&
                    subSuggestions.length === 0 &&
                    !subCategoryInput && (
                      <div className="p-3 text-gray-500 italic text-sm sm:text-base">
                        Start typing to see suggestions...
                      </div>
                    )}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Document Name
              </label>
              <input
                type="text"
                value={documentName}
                onChange={(e) =>
                  setDocumentName(
                    sanitizeTextValue(e.target.value, 'documentName')
                  )
                }
                disabled={isArchiveUploadSelected}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-[#005787] focus:border-[#005787] disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                placeholder="Enter Document Name"
              />
            </div>
          </div>

          {/* Right Column: File Upload */}
          <div className="h-full flex flex-col gap-4">
            <div
              className={`border-2 border-dashed rounded-lg h-full min-h-[160px] flex flex-col items-center justify-center p-6 text-center transition-colors cursor-pointer ${
                isDragOver
                  ? 'border-[#005787] bg-blue-50'
                  : 'border-gray-300 hover:border-[#005787]'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current.click()}
            >
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept=".pdf,.doc,.docx,.docs,.zip,.rar"
                onChange={handleFileChange}
              />

              {selectedFile ? (
                <div className="flex flex-col items-center">
                  <span className="text-[#005787] font-medium mb-1">
                    {selectedFile.name}
                  </span>
                  <span className="text-gray-500 text-sm">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFile(null);
                      if (fileInputRef.current) fileInputRef.current.value = '';
                    }}
                    className="text-red-500 text-xs mt-2 hover:underline"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <>
                  <svg
                    className="w-12 h-12 text-gray-400 mb-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    ></path>
                  </svg>
                  <p className="text-gray-600 font-medium text-sm">
                    Click to upload or drag and drop
                  </p>
                  <p className="text-gray-400 text-xs mt-1">
                    PDF, DOC, DOCX, ZIP, RAR (docs max 5MB, archives max 100MB)
                  </p>
                </>
              )}
            </div>

            <button
              onClick={handleCategorySubmit}
              disabled={isSubmitting}
              className="bg-[#005787] text-white px-4 py-2 rounded-md hover:bg-[#004a73] transition-colors w-full h-10 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <svg
                    className="w-4 h-4 animate-spin"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8z"
                    />
                  </svg>
                  Uploading...
                </>
              ) : (
                'Upload'
              )}
            </button>
          </div>
        </div>
        {visibleCategories.map((cat) => {
          const directDocuments = localDocuments?.filter((doc) => {
            const docCat = doc.documentCategory?._id || doc.documentCategory;
            const docSub =
              doc.documentSubcategory?._id || doc.documentSubcategory;
            return docCat == cat._id && !docSub;
          });

          const subcategoriesWithDocuments = (cat.children || []).filter(
            (sub) => {
              const subDocs = localDocuments?.filter((doc) => {
                const docCat =
                  doc.documentCategory?._id || doc.documentCategory;
                const docSub =
                  doc.documentSubcategory?._id || doc.documentSubcategory;
                return docCat == cat._id && docSub == sub._id;
              });
              return subDocs && subDocs.length > 0;
            }
          );

          const visibleSubcategories = subcategoriesWithDocuments;

          return (
            <div key={cat._id} className="mb-8">
              {/* Section Title */}
              <h3 className="text-gray-800 font-semibold text-lg">
                {cat.name}
              </h3>

              <hr className="my-3 border-gray-300" />

              <div className="space-y-4">
                {/* Direct Documents */}
                {directDocuments && directDocuments.length > 0 && (
                  <div className="space-y-3">
                    {directDocuments.map((doc) => (
                      <div
                        key={doc._id}
                        className="flex items-center justify-between"
                      >
                        <button
                          onClick={() => handleView(doc.location)}
                          className="text-[#005787] hover:underline text-md flex items-center gap-2"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                            ></path>
                          </svg>
                          {doc.docName || doc.name || 'Document'} {doc.createdAt ? `(${moment(doc.createdAt).format('DD MMM YYYY')})` : ''}
                        </button>
                        <div className="flex items-center">
                          <button
                            onClick={() => handleDownloadDocument(doc)}
                            disabled={doc?.isReadOnly}
                            className="mr-3 flex-shrink-0 flex items-center justify-center rounded-full disabled:opacity-70 disabled:cursor-not-allowed"
                            title="Download document"
                          >
                            <svg
                              className="w-5 h-5 stroke-blue-400 hover:stroke-blue-500"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                              />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDeleteDocument(doc._id)}
                            disabled={doc?.canDelete === false}
                            className="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-full bg-red-100 hover:bg-red-200 text-red-500 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                            title="Delete document"
                          >
                            <svg
                              className="w-3 h-3"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2.5"
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Subcategories */}
                {visibleSubcategories &&
                  visibleSubcategories.length > 0 &&
                  visibleSubcategories.map((sub) => {
                    const subDocs = localDocuments?.filter((doc) => {
                      const docCat =
                        doc.documentCategory?._id || doc.documentCategory;
                      const docSub =
                        doc.documentSubcategory?._id || doc.documentSubcategory;
                      return docCat == cat._id && docSub == sub._id;
                    });
                    return (
                      <div
                        key={sub._id}
                        className="pl-6 border-l-2 border-gray-100 mt-4"
                      >
                        <h4 className="text-gray-600 font-medium text-sm mb-2">
                          {sub.name}
                        </h4>
                        <div className="space-y-2">
                          {subDocs && subDocs.length > 0 ? (
                            subDocs.map((doc) => (
                              <div
                                key={doc._id}
                                className="flex items-center justify-between"
                              >
                                <button
                                  onClick={() => handleView(doc.location)}
                                  className="text-[#005787] hover:underline text-sm flex items-center gap-2"
                                >
                                  <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2"
                                      d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                                    ></path>
                                  </svg>
                                  {doc.docName || doc.name || 'Document'} {doc.createdAt ? `(${moment(doc.createdAt).format('DD MMM YYYY')})` : ''}
                                </button>
                                <div className="flex items-center">
                                  <button
                                    onClick={() => handleDownloadDocument(doc)}
                                    disabled={doc?.isReadOnly}
                                    className="mr-3 flex-shrink-0 flex items-center justify-center rounded-full disabled:opacity-70 disabled:cursor-not-allowed"
                                    title="Download document"
                                  >
                                    <svg
                                      className="w-5 h-5 stroke-blue-400 hover:stroke-blue-500"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                                      />
                                    </svg>
                                  </button>
                                  <button
                                    onClick={() =>
                                      handleDeleteDocument(doc._id)
                                    }
                                    disabled={doc?.canDelete === false}
                                    className="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-full bg-red-100 hover:bg-red-200 text-red-500 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                                    title="Delete document"
                                  >
                                    <svg
                                      className="w-3 h-3"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2.5"
                                        d="M6 18L18 6M6 6l12 12"
                                      />
                                    </svg>
                                  </button>
                                </div>
                              </div>
                            ))
                          ) : (
                            <p className="text-xs text-gray-400 italic">
                              No documents
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}

                {/* Fallback if no content */}
                {(!directDocuments || directDocuments.length === 0) &&
                  (!visibleSubcategories ||
                    visibleSubcategories.length === 0) && (
                    <p className="text-sm text-gray-400 italic">No documents</p>
                  )}
              </div>
            </div>
          );
        })}

        {localDocuments && localDocuments.length > 0 && (
          <div className="flex justify-end pt-4 border-t border-gray-200 mt-4">
            <button
              onClick={handleDownloadZip}
              disabled={isDownloadingZip}
              className="flex items-center gap-2 bg-[#005787] text-white px-5 py-2.5 rounded-md hover:bg-[#004a73] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isDownloadingZip ? (
                <>
                  <svg
                    className="w-4 h-4 animate-spin"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8z"
                    />
                  </svg>
                  Preparing ZIP...
                </>
              ) : (
                <>
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                  Download All as ZIP
                </>
              )}
            </button>
          </div>
        )}

        <div className="flex justify-end space-x-4">
          <button
            onClick={() => setActiveTab('information')}
            className="px-6 py-2 bg-gray-200 rounded-full hover:bg-gray-300 transition-colors"
          >
            Back
          </button>
          {loading ? (
            <button className="h-[40px] w-[200px] bg-[#004a71] rounded-full flex items-center justify-center">
              <span className="loader"></span>
            </button>
          ) : (
            <button
              // disabled={isLoading || propsData.status === "Cancelled" || propsData.status === "Withdrawn" || propsData.status === "VisaApplied" || propsData.status === "Submitted" || propsData.status === "In-Progress" || propsData.status === "Rejected" || propsData.status === "WithdrawnByClient" || propsData.status === "WithdrawnByAgent" || checklistLoading || !checklist}
              onClick={handleSubmit}
              title={
                [
                  'WithdrawnByClient',
                  'WithdrawnByAgent',
                  'In-Progress',
                  'VisaApplied',
                  'Cancelled',
                  'Finalized',
                  'Rejected',
                  'Submitted',
                ].includes(propsData.status)
                  ? 'Only Draft applications can be submitted again'
                  : ''
              }
              className={`px-6 py-2 text-white rounded-full transition-colors ${
                [
                  'WithdrawnByClient',
                  'WithdrawnByAgent',
                  'In-Progress',
                  'VisaApplied',
                  'Finalized',
                  'Rejected',
                  'Submitted',
                  'Cancelled',
                ].includes(propsData.status) ||
                checklistLoading ||
                !checklist
                  ? 'bg-[#005887ab] cursor-not-allowed'
                  : 'bg-[#005787] hover:bg-[#004a71]'
              }`}
            >
              Submit Application
            </button>
          )}
        </div>
      </motion.div>

      {showSuccess && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50">
          <motion.div
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="absolute bg-[#FFFBF5] border border-[#CAC3BB] rounded-lg shadow-lg p-8 w-full max-w-md text-center"
          >
            <motion.div
              variants={iconVariants}
              initial="hidden"
              animate="visible"
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <FiCheckCircle className="h-16 w-16 text-[#036494]" />
            </motion.div>

            <h2 className="text-xl font-semibold mb-6 text-[#1E1E1E]">
              Your application is submitted successfully
            </h2>

            <button
              onClick={handlecloseSuccess}
              className="w-full block bg-[#036494] text-white py-2 rounded-full hover:bg-[#075985] transition-colors"
            >
              Move to Invoice
            </button>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default DocumentTab;
   